import { useMemo } from 'react'
import { CUSTOM_DEMO_LIMITS, SALES_CUSTOM_LIMITS, customDemoProblems, customShiftEstimate, customUnits, type CustomDemoSettings } from '../demo/demoPlan.js'
import { seatsPerDayFor, SHIFTS_PER_WORKER, staffingTargetsFor } from '../demo/demoStaffing.js'

const INPUT_CLASS = 'w-24 border border-marine/25 px-2 py-1.5 text-sm text-ink focus:outline-none focus:border-marine'

/** The order the settings are asked in: the building, then its people, then time. */
const FIELDS: readonly (keyof CustomDemoSettings)[] = ['facilities', 'rosterOnlyFacilities', 'unitsPerFacility', 'bedsPerUnit', 'occupancyPct', 'staffingPct', 'monthsAhead']

/** A line under a setting saying what it means in the demo. */
const HINTS: Record<keyof CustomDemoSettings, string> = {
  facilities: 'Each with a full roster and every month of history; the standard three first, then facilities added for scale.',
  rosterOnlyFacilities: 'A small roster and no activity, as a large operator has. They make the facility list long, not the history.',
  unitsPerFacility: 'Unit A to D, each with the beds below.',
  bedsPerUnit: 'A skilled nursing unit is usually 20 to 45 beds.',
  occupancyPct: 'The census each unit is planned at. 85 to 95 is a typical building.',
  staffingPct: 'How much of what the staffing plan requires the roster can staff: 90 is a steady building, under 80 a short-staffed one (the open board and agency carry the rest).',
  monthsAhead: 'Published after this month, so the months taking bids have shifts. Next month is usually released already.',
}

interface CustomDemoFieldsProps {
  value: CustomDemoSettings
  onChange: (value: CustomDemoSettings) => void
  monthSpan: number
  /** A sales login's custom demo stays within a standard demo's reach. */
  forSales: boolean
}

/** What the settings come to, worked out the way the demo will build it, so the admin sees the size before submitting. */
function useCustomSummary(value: CustomDemoSettings, monthSpan: number) {
  return useMemo(() => {
    const units = customUnits(value)
    const daily = seatsPerDayFor(units)
    const seatsPerDay = daily.RN + daily.LPN + daily.CNA
    const targets = staffingTargetsFor(units, value.staffingPct)
    const workersPerFacility = targets.RN + targets.LPN + targets.CNA
    return {
      census: units.reduce((sum, unit) => sum + unit.plannedCensus, 0),
      seatsPerDay,
      byRole: daily,
      workersPerFacility,
      workers: workersPerFacility * value.facilities + 3 * value.rosterOnlyFacilities,
      shifts: customShiftEstimate(value, Number.isFinite(monthSpan) ? monthSpan : 0).total,
      reported: Math.round(workersPerFacility * SHIFTS_PER_WORKER * (Number.isFinite(monthSpan) ? monthSpan : 0) * value.facilities),
    }
  }, [value, monthSpan])
}

/**
 * The custom demo's settings (the user's request, 25 October 2026): each major part of the data with its range, the
 * problems with what is typed said in place, and what the demo comes to - the census, the seats a day, the roster and
 * the records the history will write - so a demo is sized before it is built.
 */
export function CustomDemoFields({ value, onChange, monthSpan, forSales }: CustomDemoFieldsProps) {
  const problems = customDemoProblems(value, monthSpan, forSales)
  const summary = useCustomSummary(value, monthSpan)
  const maxOf = (key: keyof CustomDemoSettings): number => (forSales ? (SALES_CUSTOM_LIMITS[key]?.max ?? CUSTOM_DEMO_LIMITS[key].max) : CUSTOM_DEMO_LIMITS[key].max)
  return (
    <fieldset className="border border-marine/15 bg-white px-4 py-3">
      <legend className="px-1 text-xs font-bold text-ink-mid">Custom demo</legend>
      <div className="grid gap-x-6 gap-y-3 sm:grid-cols-2">
        {FIELDS.map((key) => {
          const { min, label, unit } = CUSTOM_DEMO_LIMITS[key]
          const id = `custom-demo-${key}`
          return (
            <div key={key}>
              <label htmlFor={id} className="block text-xs font-bold text-ink-mid mb-1">
                {label} <span className="font-normal">({min} to {maxOf(key)}{unit ?? ''})</span>
              </label>
              <input
                id={id}
                type="number"
                inputMode="numeric"
                className={INPUT_CLASS}
                min={min}
                max={maxOf(key)}
                step={1}
                value={Number.isNaN(value[key]) ? '' : value[key]}
                onChange={(event) => onChange({ ...value, [key]: event.target.valueAsNumber })}
              />
              <p className="mt-1 text-[11px] text-ink-mid">{HINTS[key]}</p>
            </div>
          )
        })}
      </div>
      {problems.length > 0 ? (
        <ul className="mt-3 list-disc pl-5 text-[11px] font-bold text-coral">
          {problems.map((problem) => (
            <li key={problem}>{problem}</li>
          ))}
        </ul>
      ) : (
        <div className="mt-3 border-t border-marine/10 pt-3 text-xs leading-relaxed text-ink">
          <span className="font-bold">What this builds: </span>
          {value.facilities} {value.facilities === 1 ? 'facility' : 'facilities'} with history, each {value.unitsPerFacility === 1 ? 'one unit' : `${value.unitsPerFacility} units`} of {value.bedsPerUnit} beds planned at {summary.census} residents in all, needing {summary.seatsPerDay} seats a day ({summary.byRole.RN} RN, {summary.byRole.LPN} LPN, {summary.byRole.CNA} CNA) and staffed by about {summary.workersPerFacility} workers
          {value.rosterOnlyFacilities > 0 ? `; ${value.rosterOnlyFacilities} more with a roster only` : ''}. About {summary.workers.toLocaleString()} workers in all, {summary.shifts.toLocaleString()} shifts published and {summary.reported.toLocaleString()} worked shifts reported across the history.
        </div>
      )}
    </fieldset>
  )
}
